// Reading a linked picture into the bytes an export can carry.
//
// Every export has to turn a hosted address into a data URL at some point: a
// .pptx that merely points at a picture is a file PowerPoint blocks as external
// media, and the SVG a Custom HTML/CSS layout is rasterized through cannot
// fetch anything at all. Both paths had their own copy of "fetch it and read
// it", and both learned the same lesson the hard way, so the lesson lives here.
//
// What goes wrong, and why the retry is not superstition:
//
//   * **A cached 404.** The asset host serves "not found" with
//     `max-age=14400`, so an address asked for once before its file finished
//     uploading reads as missing for the next four hours. The editor shows the
//     picture perfectly by then, because an `<img>` and a `fetch` do not share
//     a cache entry — and the export, reading the stale one, drops it.
//   * **A response cached without CORS headers**, which the same host returns
//     to any request that carries no `Origin`.
//
// Both look identical from here: the first read fails and a read that steps
// over the cache succeeds.

/**
 * How long one picture gets before the export gives up on it.
 *
 * Without this a request that never settles stalls the whole export: pictures
 * are read in parallel and the export waits on all of them.
 */
export const IMAGE_FETCH_TIMEOUT_MS = 20000;

const readAsDataUrl = (blob) => new Promise((resolve, reject) => {
  if (typeof FileReader === 'undefined') { reject(new Error('no FileReader')); return; }
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error('could not read the picture'));
  reader.readAsDataURL(blob);
});

/**
 * One attempt. Throws with `canRetry` set when the failure is the kind a
 * cache-skipping second attempt can fix.
 */
const readOnce = async (request, url, cache) => {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), IMAGE_FETCH_TIMEOUT_MS)
    : null;
  try {
    const init = { mode: 'cors', cache };
    if (controller) init.signal = controller.signal;
    const response = await request(url, init);
    if (!response.ok) {
      const error = new Error(`the host answered ${response.status}`);
      error.canRetry = true;
      throw error;
    }
    const blob = await response.blob();
    // A wrong content type is a fact about the address, not about the cache, so
    // it is deliberately left un-retryable.
    if (!/^image\//i.test(blob.type)) throw new Error(`that address is ${blob.type || 'not a picture'}`);
    return await readAsDataUrl(blob);
  } catch (error) {
    // A refused read — no `Access-Control-Allow-Origin` — arrives as a bare
    // TypeError with no status to inspect, and it is one of the two failures a
    // cache-skipping retry fixes.
    if (error instanceof TypeError) error.canRetry = true;
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/**
 * Read a linked picture as a data URL.
 *
 * Read once the ordinary way, then once more stepping over the browser's cache.
 * Throws the first failure when both attempts fail — callers decide whether a
 * missing picture is fatal, and they differ.
 *
 * `requestUrl` is what actually gets fetched, for callers that route an address
 * through a proxy; `url` stays the address to report.
 */
export const fetchRemoteImage = async (url, { fetchImpl, requestUrl } = {}) => {
  const request = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!request) throw new Error('this environment cannot fetch');
  const target = requestUrl || url;

  let firstError = null;
  // 'default' is the browser's ordinary cache behaviour. 'reload' is the same
  // request with the cache stepped over.
  for (const cache of ['default', 'reload']) {
    try {
      return await readOnce(request, target, cache);
    } catch (error) {
      firstError = firstError || error;
      if (!error.canRetry) break;
    }
  }
  throw firstError;
};

/**
 * What to tell someone about a read that failed, given how it failed.
 *
 * Every failure here used to end with "allow this site in its CORS rules",
 * whatever had actually happened. A 404 from a mistyped path, an address that
 * turned out to be an HTML page, and a genuine cross-origin refusal all sent
 * the reader to reconfigure a bucket that was usually already correct — and a
 * bucket they had *just* configured correctly is exactly when this message is
 * least welcome and least true.
 *
 * CORS is named only for the failure that actually looks like CORS: both
 * attempts refused with no status to inspect, including the one that stepped
 * over the browser's cache.
 */
export const describeReadFailure = (error) => {
  const message = error?.message || '';
  if (/^the host answered 404$/.test(message)) {
    return 'The host says there is nothing at that address — check the link rather than the CORS rules.';
  }
  if (/^the host answered /.test(message)) {
    return 'The host refused to serve the file. That is a problem with the file or the host, not with this app\'s access to it.';
  }
  if (/^that address is /.test(message)) {
    return 'That address does not serve a picture. Copy the address of the image file itself.';
  }
  if (/timed out|aborted|abort/i.test(message)) {
    return 'The request did not finish in time. Try the export again; a slow or interrupted connection is the usual cause.';
  }
  return 'The request was refused outright, twice, which is what a cross-origin block looks like. '
    + 'If the picture is in a bucket you control, allow this site in its CORS rules.';
};

/**
 * The same read, for callers that want a picture or nothing.
 *
 * A failure is not silent: the address and the reason are logged, because a
 * picture that vanishes from an export with no trace is the hardest kind of
 * bug to report — the export "succeeded" and the slide is simply missing
 * something.
 *
 * `quiet` is for the one case where a failure is routine and expected: a
 * caller trying a preferred address before a fallback, like a video's
 * `maxresdefault` thumbnail, which simply does not exist for many clips. A
 * warning there would be noise, and noise is how a real warning gets ignored.
 */
export const fetchRemoteImageOrNull = async (
  url,
  { fetchImpl, requestUrl, label = 'export', quiet = false } = {}
) => {
  try {
    const dataUrl = await fetchRemoteImage(url, { fetchImpl, requestUrl });
    return dataUrl && dataUrl.startsWith('data:image') ? dataUrl : null;
  } catch (error) {
    if (!quiet) {
      console.warn(
        `[${label}] Could not read ${url} — ${error.message}. That picture will be `
        + `missing from the exported file. ${describeReadFailure(error)}`
      );
    }
    return null;
  }
};
